import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { 
    Box, Typography, Paper, Grid, Chip, Button, CircularProgress, 
    Divider, IconButton, Snackbar, Alert, Dialog, DialogContent, 
    Breadcrumbs, Link, Card, CardMedia, Tooltip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KingBedIcon from '@mui/icons-material/KingBed';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import LayersIcon from '@mui/icons-material/Layers';
import ApartmentIcon from '@mui/icons-material/Apartment';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import CloseIcon from '@mui/icons-material/Close';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BuildIcon from '@mui/icons-material/Build';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import StarIcon from '@mui/icons-material/Star';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

import { RoomService } from '../../api/services/roomService';
import RoomFormModal from '../../components/RoomFormModal';
import { useCurrency, formatCurrency } from '../../utils/currency';

export default function RoomDetails() {
    const { currency } = useCurrency();
    const { id } = useParams();
    const navigate = useNavigate();
    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    
    // Lightbox image viewer
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [activeImageIndex, setActiveImageIndex] = useState(0);

    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = authData.tenantId || '1';
    const { tenants } = useSelector((state) => state.auth || { tenants: [] });
    const tenant = tenants.find(t => String(t.tenantId) === String(tenantId) || String(t.id) === String(tenantId) || String(t._id) === String(tenantId));
    const hotelStatus = tenant?.status || 'Active';
    const isGeometryLocked = hotelStatus === 'Active';

    const fetchRoomDetails = useCallback(async () => {
        setLoading(true);
        try {
            const response = await RoomService.getById(id, tenantId);
            setRoom(response.data || response);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch room details:', err);
            setError(err.message || 'Failed to load room details. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [id, tenantId]);

    useEffect(() => {
        if (id) {
            fetchRoomDetails();
        }
    }, [id, fetchRoomDetails]);

    const handleSaveRoom = async (savedRoomData) => {
        try {
            await RoomService.save(savedRoomData, tenantId);
            setNotification({ open: true, message: 'Room updated successfully!', severity: 'success' });
            setEditModalOpen(false);
            fetchRoomDetails();
        } catch (err) {
            console.error('Failed to update room:', err);
            setNotification({ open: true, message: err.message || 'Failed to update room', severity: 'error' });
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '65vh' }}>
                <CircularProgress size={48} thickness={4} />
                <Typography sx={{ mt: 2.5, fontWeight: 500 }} color="text.secondary">Fetching luxury room details...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 5, textAlign: 'center', maxWidth: 600, mx: 'auto', mt: 4 }}>
                <Paper sx={{ p: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                    <Typography color="error.main" variant="h5" fontWeight="bold" gutterBottom>Unable to Load Room</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>{error}</Typography>
                    <Button variant="contained" onClick={() => navigate('/room-management')} startIcon={<ArrowBackIcon />}>
                        Return to Room Management
                    </Button>
                </Paper>
            </Box>
        );
    }

    if (!room) return null;

    const roomTypeName = room.roomType && typeof room.roomType === 'object' 
        ? room.roomType.name 
        : (room.type && typeof room.type === 'object' ? room.type.name : (room.type || room.roomType || 'Standard Room'));

    const floorDisplay = room.floor && typeof room.floor === 'object'
        ? (room.floor.label || `Floor ${room.floor.number}`)
        : (room.floor ? (String(room.floor).toLowerCase().startsWith('floor') ? room.floor : `Floor ${room.floor}`) : 'Floor N/A');

    const wingDisplay = room.wing && typeof room.wing === 'object'
        ? (room.wing.name || room.wing.label || 'Wing N/A')
        : (room.wing ? (String(room.wing).toLowerCase().includes('wing') ? room.wing : `${room.wing} Wing`) : 'Wing N/A');

    const rawStatus = typeof room.status === 'object' ? (room.status?.name || 'Available') : (room.status || 'Available');
    
    const getStatusTheme = (st) => {
        const s = String(st).toLowerCase();
        if (s.includes('avail') || s.includes('clean') || s.includes('ready')) {
            return { bg: '#ECFDF5', color: '#065F46', border: '#A7F3D0', icon: <CheckCircleIcon fontSize="small" /> };
        }
        if (s.includes('ooo') || s.includes('order') || s.includes('maint')) {
            return { bg: '#FEF2F2', color: '#991B1B', border: '#FECACA', icon: <BuildIcon fontSize="small" /> };
        }
        if (s.includes('dirty') || s.includes('clean')) {
            return { bg: '#FFFBEB', color: '#92400E', border: '#FDE68A', icon: <CleaningServicesIcon fontSize="small" /> };
        }
        return { bg: '#EFF6FF', color: '#1E40AF', border: '#BFDBFE', icon: <MeetingRoomIcon fontSize="small" /> };
    };

    const statusTheme = getStatusTheme(rawStatus);
    const roomImages = Array.isArray(room.images) && room.images.length > 0 ? room.images : [];
    const maxAdults = room.maxAdults ?? room.maxOccupancy ?? 2;
    const maxKids = room.maxKids ?? 0;
    const totalOccupancy = Number(maxAdults) + Number(maxKids);
    const amenities = room.amenities || room.features || [];

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
            {/* Top Navigation & Breadcrumbs */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1, fontSize: '0.875rem' }}>
                    <Link 
                        underline="hover" 
                        color="inherit" 
                        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} 
                        onClick={() => navigate('/room-management')}
                    >
                        <MeetingRoomIcon sx={{ mr: 0.5, fontSize: 18 }} />
                        Room Management
                    </Link>
                    <Typography color="text.primary" fontWeight="bold">
                        Room {room.roomNumber || room.number}
                    </Typography>
                </Breadcrumbs>
            </Box>

            {/* Header Hero Banner */}
            <Paper 
                elevation={0}
                sx={{ 
                    p: { xs: 2.5, md: 3.5 }, 
                    mb: 3.5, 
                    borderRadius: 3.5, 
                    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                    color: '#ffffff',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    flexWrap: 'wrap', 
                    gap: 2.5,
                    boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.25)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <IconButton 
                        onClick={() => navigate('/room-management')} 
                        sx={{ 
                            bgcolor: 'rgba(255,255,255,0.12)', 
                            color: '#ffffff',
                            backdropFilter: 'blur(8px)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } 
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="h4" fontWeight="800" letterSpacing="-0.5px">
                                Room {room.roomNumber || room.number}
                            </Typography>
                            <Chip 
                                icon={statusTheme.icon}
                                label={rawStatus} 
                                sx={{ 
                                    bgcolor: statusTheme.bg, 
                                    color: statusTheme.color, 
                                    borderColor: statusTheme.border,
                                    border: '1px solid',
                                    fontWeight: 700,
                                    px: 0.5,
                                    fontSize: '0.85rem'
                                }} 
                            />
                        </Box>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
                            {roomTypeName} • {floorDisplay} • {wingDisplay}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button 
                        variant="contained" 
                        startIcon={<EditIcon />}
                        onClick={() => setEditModalOpen(true)}
                        disabled={isGeometryLocked}
                        sx={{ 
                            borderRadius: 2.5,
                            px: 3,
                            py: 1.2,
                            fontWeight: 700,
                            background: isGeometryLocked 
                                ? undefined 
                                : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                            boxShadow: '0 4px 14px 0 rgba(37,99,235,0.39)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                            }
                        }}
                    >
                        Edit Room
                    </Button>
                </Box>
            </Paper>

            {/* Quick Metrics 4-Card Row */}
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ 
                        p: 2.5, 
                        borderRadius: 3, 
                        border: '1px solid #e2e8f0', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.06)' }
                    }}>
                        <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#EEF2FF', color: '#4F46E5', display: 'flex' }}>
                            <AttachMoneyIcon fontSize="large" />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                                Base Rate / Night
                            </Typography>
                            <Typography variant="h6" fontWeight="800" color="text.primary">
                                {formatCurrency(room.baseRate || room.rate || 0, currency)}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ 
                        p: 2.5, 
                        borderRadius: 3, 
                        border: '1px solid #e2e8f0', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.06)' }
                    }}>
                        <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#F0FDF4', color: '#16A34A', display: 'flex' }}>
                            <KingBedIcon fontSize="large" />
                        </Box>
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                                Room Category
                            </Typography>
                            <Typography variant="h6" fontWeight="800" color="text.primary" noWrap title={roomTypeName}>
                                {roomTypeName}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ 
                        p: 2.5, 
                        borderRadius: 3, 
                        border: '1px solid #e2e8f0', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.06)' }
                    }}>
                        <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#FEF3C7', color: '#D97706', display: 'flex' }}>
                            <LocationCityIcon fontSize="large" />
                        </Box>
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                                Position
                            </Typography>
                            <Typography variant="h6" fontWeight="800" color="text.primary" noWrap>
                                {floorDisplay}
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ 
                        p: 2.5, 
                        borderRadius: 3, 
                        border: '1px solid #e2e8f0', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2,
                        transition: 'all 0.2s ease',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.06)' }
                    }}>
                        <Box sx={{ p: 1.5, borderRadius: 2.5, bgcolor: '#FAF5FF', color: '#9333EA', display: 'flex' }}>
                            <PeopleIcon fontSize="large" />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                                Max Capacity
                            </Typography>
                            <Typography variant="h6" fontWeight="800" color="text.primary">
                                {totalOccupancy} Guests ({maxAdults}A {maxKids > 0 ? `+ ${maxKids}C` : ''})
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Main Content Layout */}
            <Grid container spacing={3.5}>
                {/* Left Column: Gallery & Amenities */}
                <Grid item xs={12} lg={8}>
                    {/* Visual Media Showcase */}
                    <Paper sx={{ p: 3.5, borderRadius: 3.5, mb: 3.5, border: '1px solid #e2e8f0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <PhotoLibraryIcon color="primary" />
                                <Typography variant="h6" fontWeight="800">
                                    Room Visual Gallery
                                </Typography>
                                <Chip 
                                    label={`${roomImages.length} ${roomImages.length === 1 ? 'Photo' : 'Photos'}`} 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                    sx={{ fontWeight: 700 }}
                                />
                            </Box>
                            {roomImages.length === 0 && (
                                <Button 
                                    size="small" 
                                    startIcon={<AddPhotoAlternateIcon />}
                                    onClick={() => setEditModalOpen(true)}
                                >
                                    Upload Photos
                                </Button>
                            )}
                        </Box>

                        {roomImages.length > 0 ? (
                            <Box>
                                {/* Featured Hero Image */}
                                <Card 
                                    elevation={0}
                                    sx={{ 
                                        position: 'relative', 
                                        borderRadius: 3, 
                                        overflow: 'hidden', 
                                        cursor: 'pointer',
                                        height: { xs: 240, sm: 340 },
                                        mb: 2,
                                        border: '1px solid #e2e8f0',
                                        '&:hover .overlay': { opacity: 1 }
                                    }}
                                    onClick={() => { setActiveImageIndex(0); setLightboxOpen(true); }}
                                >
                                    <CardMedia
                                        component="img"
                                        image={roomImages[0]}
                                        alt={`Room ${room.roomNumber} primary`}
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <Chip 
                                        icon={<StarIcon sx={{ fontSize: '14px !important', color: '#F59E0B !important' }} />}
                                        label="Cover Photo" 
                                        size="small"
                                        sx={{ 
                                            position: 'absolute', 
                                            top: 14, 
                                            left: 14, 
                                            bgcolor: 'rgba(0,0,0,0.75)', 
                                            color: '#ffffff',
                                            backdropFilter: 'blur(6px)',
                                            fontWeight: 700,
                                            fontSize: '0.75rem'
                                        }} 
                                    />
                                    <Box 
                                        className="overlay"
                                        sx={{ 
                                            position: 'absolute', 
                                            inset: 0, 
                                            bgcolor: 'rgba(15, 23, 42, 0.45)', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            opacity: 0,
                                            transition: 'opacity 0.2s',
                                            color: '#ffffff',
                                            fontWeight: 600
                                        }}
                                    >
                                        <Typography variant="subtitle1" fontWeight="bold">Click to Enlarge Gallery</Typography>
                                    </Box>
                                </Card>

                                {/* Thumbnails Row */}
                                {roomImages.length > 1 && (
                                    <Box sx={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', 
                                        gap: 1.5 
                                    }}>
                                        {roomImages.map((img, idx) => (
                                            <Card
                                                key={idx}
                                                elevation={0}
                                                onClick={() => { setActiveImageIndex(idx); setLightboxOpen(true); }}
                                                sx={{
                                                    height: 90,
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    border: idx === 0 ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                                    overflow: 'hidden',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        transform: 'scale(1.04)',
                                                        boxShadow: '0 6px 16px rgba(0,0,0,0.12)'
                                                    }
                                                }}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    image={img}
                                                    alt={`Photo ${idx + 1}`}
                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            </Card>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        ) : (
                            <Box 
                                sx={{ 
                                    p: 5, 
                                    textAlign: 'center', 
                                    bgcolor: '#f8fafc', 
                                    borderRadius: 3,
                                    border: '2px dashed #cbd5e1' 
                                }}
                            >
                                <PhotoLibraryIcon sx={{ fontSize: 52, color: '#94a3b8', mb: 1.5 }} />
                                <Typography variant="h6" fontWeight="bold" color="text.secondary">
                                    No Room Photos Uploaded
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 400, mx: 'auto' }}>
                                    Upload luxury high-res photos to showcase this room in the guest booking engine and front-desk portal.
                                </Typography>
                                <Button 
                                    variant="outlined" 
                                    startIcon={<AddPhotoAlternateIcon />}
                                    onClick={() => setEditModalOpen(true)}
                                    sx={{ borderRadius: 2 }}
                                >
                                    Upload Room Images
                                </Button>
                            </Box>
                        )}
                    </Paper>

                    {/* Amenities & Features */}
                    <Paper sx={{ p: 3.5, borderRadius: 3.5, border: '1px solid #e2e8f0' }}>
                        <Typography variant="h6" fontWeight="800" mb={2}>
                            Room Amenities & Room Inclusions
                        </Typography>
                        <Divider sx={{ mb: 2.5 }} />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
                            {amenities.length > 0 ? (
                                amenities.map((amenity, index) => {
                                    const label = typeof amenity === 'object' ? (amenity.name || amenity.label || JSON.stringify(amenity)) : String(amenity);
                                    return (
                                        <Chip 
                                            key={index} 
                                            label={label} 
                                            variant="outlined"
                                            sx={{ 
                                                fontWeight: 600,
                                                borderRadius: 2,
                                                px: 1,
                                                py: 2,
                                                bgcolor: '#F8FAFC',
                                                borderColor: '#CBD5E1',
                                                '&:hover': { bgcolor: '#F1F5F9' }
                                            }} 
                                        />
                                    );
                                })
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No specific amenities configured for this room.
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Right Column: Key Specifications & Details */}
                <Grid item xs={12} lg={4}>
                    <Paper sx={{ p: 3.5, borderRadius: 3.5, mb: 3.5, border: '1px solid #e2e8f0' }}>
                        <Typography variant="h6" fontWeight="800" mb={2.5}>
                            Room Specifications
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <MeetingRoomIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary">Room Identifier</Typography>
                                </Box>
                                <Typography variant="body1" fontWeight="bold">{room.roomNumber || room.number}</Typography>
                            </Box>
                            <Divider />

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <KingBedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary">Category</Typography>
                                </Box>
                                <Typography variant="body1" fontWeight="bold">{roomTypeName}</Typography>
                            </Box>
                            <Divider />

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <LayersIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary">Floor Level</Typography>
                                </Box>
                                <Typography variant="body1" fontWeight="bold">{floorDisplay}</Typography>
                            </Box>
                            <Divider />

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <ApartmentIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary">Property Wing</Typography>
                                </Box>
                                <Typography variant="body1" fontWeight="bold">{wingDisplay}</Typography>
                            </Box>
                            <Divider />

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary">Adult Capacity</Typography>
                                </Box>
                                <Typography variant="body1" fontWeight="bold">{maxAdults} Adults</Typography>
                            </Box>
                            <Divider />

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <ChildCareIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary">Child Capacity</Typography>
                                </Box>
                                <Typography variant="body1" fontWeight="bold">{maxKids} Children</Typography>
                            </Box>
                            <Divider />

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <AttachMoneyIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    <Typography variant="body2" color="text.secondary">Nightly Tariff</Typography>
                                </Box>
                                <Typography variant="body1" fontWeight="800" color="primary.main">
                                    {formatCurrency(room.baseRate || room.rate || 0, currency)}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Lightbox Dialog for Full Image Viewing */}
            {lightboxOpen && roomImages.length > 0 && (
                <Dialog
                    open={lightboxOpen}
                    onClose={() => setLightboxOpen(false)}
                    maxWidth="lg"
                    PaperProps={{
                        sx: {
                            bgcolor: 'rgba(15, 23, 42, 0.95)',
                            color: '#ffffff',
                            borderRadius: 3,
                            overflow: 'hidden',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)'
                        }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Room {room.roomNumber || room.number} — Photo {activeImageIndex + 1} of {roomImages.length}
                        </Typography>
                        <IconButton onClick={() => setLightboxOpen(false)} sx={{ color: '#ffffff' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
                        <Box
                            component="img"
                            src={roomImages[activeImageIndex]}
                            alt={`Room preview ${activeImageIndex + 1}`}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '75vh',
                                objectFit: 'contain'
                            }}
                        />

                        {roomImages.length > 1 && (
                            <>
                                <IconButton
                                    onClick={() => setActiveImageIndex((prev) => (prev === 0 ? roomImages.length - 1 : prev - 1))}
                                    sx={{
                                        position: 'absolute',
                                        left: 16,
                                        bgcolor: 'rgba(0,0,0,0.6)',
                                        color: '#ffffff',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' }
                                    }}
                                >
                                    <NavigateBeforeIcon fontSize="large" />
                                </IconButton>
                                <IconButton
                                    onClick={() => setActiveImageIndex((prev) => (prev === roomImages.length - 1 ? 0 : prev + 1))}
                                    sx={{
                                        position: 'absolute',
                                        right: 16,
                                        bgcolor: 'rgba(0,0,0,0.6)',
                                        color: '#ffffff',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' }
                                    }}
                                >
                                    <NavigateNextIcon fontSize="large" />
                                </IconButton>
                            </>
                        )}
                    </DialogContent>
                </Dialog>
            )}

            {/* Room Edit Modal */}
            {editModalOpen && (
                <RoomFormModal
                    open={editModalOpen}
                    editingRoom={room}
                    data={room}
                    onClose={() => setEditModalOpen(false)}
                    onSave={handleSaveRoom}
                />
            )}

            {/* Notification Toast */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() => setNotification({ ...notification, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%', fontWeight: 600 }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
